import { Divider, Form, Input, Skeleton, Spin } from 'antd';
import { RuleObject } from 'antd/es/form';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import KeyIcon from 'assets/Icons/KeyIcon';
import clsx from 'clsx';
import parsePhoneNumberFromString, { ParseError } from 'libphonenumber-js';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAddresses } from '@/hooks/useAddresses';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { CustomSelect } from '@/components/shared/CustomSelect';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { IntlTelInput } from '@/components/shared/IntlTelInput';
import { notify } from '@/components/shared/notify';

import { getGlobalCountry } from '@/atoms/countryState';
import { user } from '@/atoms/user';
import { createAddressService, updateAddress } from '@/services/Address';
import { getProductDetails } from '@/services/Products';
import { subscribeProduct } from '@/services/Subscribe';
import { getUserProfile } from '@/services/user';
import { Address, AddressPayload } from '@/utils/interfaces/Manage-Addresses';
import { IProduct } from '@/utils/interfaces/Product';
import { User } from '@/utils/interfaces/User';

interface CartContactFormState {
  visibleLogin: boolean;
  visibleForgotPassword: boolean;
  selectedAddress: Address | null;
  isChanged: boolean;
  updateLoading: boolean;
  paymentOption?: 'razorpay' | 'cash_on_delivery' | 'wallet';
  error: string | null;
  loginError: string;
  checkEmailLoading: boolean;
  checkEmailError: string;
  phoneError?: string;
}

interface IFormData {
  fullname: string;
  email: string;
  postalcode: string;
  phone: string;
  country: string;
  city: string;
  state: string;
  address: string;
  password: string;
  parentEmail?: string;
  parentName?: string;
  parentPhone?: string;
  phoneCountryCode?: string;
  phonePrefix?: string;
}

const SubsctiptionCheckout = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const productId = searchParams.get('productId');

  const {
    getAllCountries,
    countries,
    getStates,
    states,
    cities,
    getCities,
    fieldLoading,
    getAllAddresses,
    address,
    loading,
  } = useAddresses();

  const screens = useBreakpoint();

  const [state, setState] = useState<CartContactFormState>({
    visibleLogin: false,
    visibleForgotPassword: false,
    selectedAddress: null,
    isChanged: false,
    updateLoading: false,
    paymentOption: 'razorpay',
    error: null,
    loginError: '',
    checkEmailLoading: false,
    checkEmailError: '',
    phoneError: '',
  });
  const globalCountry = getGlobalCountry();
  const { selectedAddress, isChanged } = state;
  const [formData, setFormData] = useState<IFormData>({
    fullname: '',
    email: '',
    postalcode: '',
    phone: '',
    country: '',
    city: '',
    state: '',
    address: '',
    password: '',
    phoneCountryCode: '',
    phonePrefix: '',
  });
  const [form] = Form.useForm();
  const userData: User | undefined = useRecoilValue(user);
  const [product, setProduct] = useState<IProduct>();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [, setUserData] = useRecoilState(user);

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    form.setFieldsValue({ [name]: value });
  };

  const handleSelectChange = (value: string | number, name: string) => {
    if (name === 'country') {
      setFormData({
        ...formData,
        [name as string]: value,
        state: '',
        city: '',
      });
      setState((prev) => ({ ...prev, isChanged: true }));
    } else if (name === 'state') {
      setFormData({ ...formData, [name as string]: value, city: '' });
      setState((prev) => ({ ...prev, isChanged: true }));
    } else {
      setFormData({ ...formData, [name as string]: value });
      setState((prev) => ({ ...prev, isChanged: true }));
    }
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string | number },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const affixContent = (
    <>
      <div
        className={clsx(
          !screens.lg && 'mb-0',
          'bg-[#101010] rounded-lg w-full',
        )}
      >
        <div className='flex gap-y-2 flex-col pt-5 md:pt-8 px-4 '>
          <span className='text-lg font-semibold tracking-wider'>
            ORDER SUMMARY
          </span>
          <div className='  flex flex-col gap-y-4 mt-3 md:mt-5'>
            <ShouldRender check={!loading}>
              <div className='flex items-center gap-5'>
                <div
                  className={clsx(
                    'px-2 pt-[5px] pb-[1px] items-end',

                    'w-20  flex  bg-imageCardBgColorCart rounded-md',
                  )}
                >
                  <Image
                    src={product?.thumbnail_image ?? ''}
                    alt='Product'
                    width={160}
                    height={160}
                    className='object-contain w-full h-full'
                  />
                </div>
                <div className='flex justify-between items-center gap-x-3 md:gap-x-5 w-full'>
                  <div className='flex flex-col gap-y-[2px]'>
                    {/* <div>
                      <div className='flex text-textGrayColor font-medium text-xs  gap-2'>
                        <span className=''>Qty. </span>
                        <span className=''>0</span>
                      </div>
                    </div> */}
                    <span className='text-xs md:text-base leading-tight'>
                      {product?.name}
                    </span>
                    <span className='font-medium text-[10px] mt-1 text-textGrayColor'>
                      {product?.book_type}
                    </span>
                  </div>
                  <div className='flex justify-end flex-col items-end'>
                    <div className='font-light text-[12px] md:text-sm whitespace-nowrap  text-textGrayColor line-through '>
                      ₹ {product?.stroked_price}
                    </div>
                    <div className='font-medium text-[14px] md:text-base text-white whitespace-nowrap'>
                      ₹ {product?.main_price}
                    </div>
                  </div>
                </div>
              </div>
            </ShouldRender>

            <ShouldRender check={loading}>
              <div className='flex gap-x-4 overflow-y-auto'>
                {/* Avatar Skeleton */}
                <Skeleton.Button
                  active
                  shape='square'
                  size='small'
                  className='rounded-md w-[52px] h-[52px]'
                />

                {/* Name Skeleton */}
                <div className='w-full flex flex-col'>
                  <span className='w-1/3'>
                    <Skeleton.Button
                      active
                      shape='square'
                      size='small'
                      className='h-3 w-full'
                    />
                  </span>

                  <Skeleton.Input
                    active
                    size='small'
                    style={{ width: '80%' }}
                    className='h-5'
                  />
                </div>
                <div className='text-sm flex w-1/2 items-center text-textDarkGrayColor'>
                  <Skeleton.Button
                    active
                    size='small'
                    className='h-full py-2'
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>

        <div className={clsx('gap-y-4 lg:gap-y-5 ', 'flex flex-col  pb-5')}>
          <Divider
            style={{
              margin: '2px 0',
              color: '#1C1C1C',
              border: '1px solid #1C1C1C',
            }}
            className='text-cartBgColor bg-cartBgColor mt-5'
          />
          <div
          // className={montserrat.className}
          >
            <div className='flex justify-between items-center px-8 '>
              <p className='text-sm text-white font-medium'>To Pay</p>
              <div className='flex items-center gap-2'>
                <KeyIcon />
                <p className='text-2xl/6 font-bold text-white'>01</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={clsx(!screens.lg && 'fixed bottom-0 left-0 z-40', 'w-full ')}
      ></div>
    </>
  );

  const handleInput = useCallback(
    (fieldName: string, notIndia: boolean) =>
      async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        const cleanedValue = value.replace(/\s/g, '');
        const sanitizedValue = notIndia
          ? cleanedValue.replace(/\D/g, '')
          : cleanedValue;

        setFormData({
          ...formData,
          [fieldName]: sanitizedValue,
        });

        // if (fieldName === 'postalcode' && /^[0-9]{6}$/.test(sanitizedValue)) {
        //   const { state, city } = await fetchStateAndCityByPostalCode({
        //     postalCode: sanitizedValue,
        //     country: formData.country,
        //   });
        //   handleApiCall('state_id', state);
        //   handleApiCall('city_id', city);

        //   setFormData((prev) => ({
        //     ...prev,
        //     state,
        //     city,
        //   }));

        //   form.setFieldsValue({
        //     state,
        //     city,
        //   });
        // }
      },
    [formData],
  );

  const getProductsDetailsHandle = async (slug: string) => {
    try {
      const result = await getProductDetails(slug);
      setProduct(result.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getValue = (
    data: { label: string; value: string | number }[] | undefined,
    key: string,
  ) =>
    typeof formData[key as keyof typeof formData] === 'number'
      ? formData[key as keyof typeof formData]
      : String(
          data?.find(
            (item) => item.label === formData[key as keyof typeof formData],
          )?.value as string,
        );

  const submitHandle = async () => {
    try {
      const validationErrors = {};
      await form.validateFields();

      if (Object.values(validationErrors).some((error) => error)) {
        return;
      }

      setSubmitLoading(true);
      const selectedState = getValue(states, 'state');
      const selectedCity = getValue(cities, 'city');
      const selectedCountry = getValue(countries, 'country');

      let addr = address;

      const data = {
        user_id: userData?.id,
        name: userData?.name as string,
        address: formData?.address,
        country_id: selectedCountry as string,
        city_id: selectedCity as string,
        state_id: selectedState as string,
        postal_code: formData?.postalcode,
        phone: formData?.phone as string,
        country_code: formData?.phonePrefix,
        iso2: formData?.phoneCountryCode,
      };

      if (!addr || addr.length === 0) {
        const result = await createAddressService({
          ...data,
          user_id: data.user_id || 0,
          set_default: 0,
        });

        addr = [result.data];
      }

      if (isChanged && addr?.[0]?.id) {
        await updateAddress({
          ...data,
          id: Number(addr?.[0]?.id),
          set_default: 0,
        } as AddressPayload);
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (addr?.[0]?.id && productId) {
        const result = await subscribeProduct({
          address_id: addr[0]?.id,
          product_id: parseInt(productId),
        });

        const data = await getUserProfile();
        setUserData(data?.data);

        router.replace(`/order-successful?orderId=${result?.data?.id}`);

        setSubmitLoading(false);
      }

      setSubmitLoading(false);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
      setSubmitLoading(false);
    }
  };

  const handleValuesChange = (changedValues: any) => {
    setFormData((prevData) => ({
      ...prevData,
      ...changedValues,
    }));
  };

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    setFormData((prev) => ({
      ...prev,
      phone: cleanedValue,
      phoneCountryCode,
      phonePrefix,
    }));

    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        '+' + phonePrefix + cleanedValue,
      );

      if (!cleanedValue.trim()) {
        const errorMsg = 'Phone number is required';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else if (!parsedPhoneNumber) {
        const errorMsg = 'Invalid phone number format';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else if (!parsedPhoneNumber.isValid()) {
        const errorMsg =
          'Invalid phone number. Please check the number and try again.';
        setState((prev) => ({
          ...prev,
          phoneError: errorMsg,
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [errorMsg],
          },
        ]);
      } else {
        setState((prev) => ({
          ...prev,
          phoneError: '',
        }));
        form.setFields([
          {
            name: 'phone',
            errors: [],
          },
        ]);
      }
    } catch (error) {
      const errorMsg =
        error instanceof ParseError
          ? error.message
          : 'An error occurred while validating the phone number. Please try again.';

      setState((prev) => ({
        ...prev,
        phoneError: errorMsg,
      }));
      form.setFields([
        {
          name: 'phone',
          errors: [errorMsg],
        },
      ]);
    }
  };

  useEffect(() => {
    getAllCountries();
  }, [selectedAddress]);

  useEffect(() => {
    if (formData?.country && countries) {
      const selectedCountry =
        typeof formData?.country === 'string'
          ? String(
              countries?.find((item) => item.label === formData.country)?.value,
            )
          : formData?.country;
      if (selectedCountry && selectedCountry !== 'undefined') {
        getStates(selectedCountry);
      }
    }
  }, [formData?.country, countries, selectedAddress]);

  useEffect(() => {
    const finalizeFormSetup = async () => {
      if (userData) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          email: userData.email,
        }));
        form.setFieldValue('email', userData.email);
        getAllAddresses();
      }

      setFormData((prevFormData) => ({
        ...prevFormData,
        country: 'India',
      }));
      form.setFieldValue('country', 'India');
    };

    finalizeFormSetup();
  }, [userData]);

  useEffect(() => {
    if (formData?.state && states) {
      const selectedState =
        typeof formData?.state === 'string'
          ? String(states?.find((item) => item.label === formData.state)?.value)
          : formData?.state;
      if (selectedState && selectedState !== 'undefined') {
        getCities(selectedState);
      }
    }
  }, [formData?.state, states, selectedAddress]);

  useEffect(() => {
    if (userData && address) {
      if (address.length > 0) {
        setFormData((prevData) => ({
          ...prevData,
          ...address[0],
          password: address[0]?.password || '',
        }));

        Object.entries(address[0] as object).forEach(([key, value]) => {
          form.setFieldsValue({ [key]: value });
        });
      }
      setFormData((prevFormData) => ({
        ...prevFormData,
        email: userData.email,
      }));
      form.setFieldsValue({ email: userData.email });
    }
  }, [selectedAddress, userData, address, form]);

  useEffect(() => {
    productId && getProductsDetailsHandle(productId as string);
  }, [productId]);

  useEffect(() => {
    if (address?.length) {
      const isAddressChanged =
        address?.[0] &&
        Object.entries(address?.[0]).some(
          ([key, value]) => value !== (formData as any)[key],
        );
      setState((prevState) => ({
        ...prevState,
        isChanged: isAddressChanged as boolean,
      }));
    }
  }, [address, formData]);

  return (
    <div className='lg:grid-cols-3 grid w-full  relative mt-20'>
      <div className='col-span-2 lg:border-r-2 border-borderDarkGray '>
        <div className=' px-4 flex flex-col gap-y-4'>
          <Form
            form={form}
            onFinish={submitHandle}
            // disabled={orderLoading}
            layout='vertical'
            initialValues={formData}
            onValuesChange={handleValuesChange}
          >
            <div className='flex md:px-8 flex-col gap-y-5 text-fadedWhite'>
              {/* Billing Address Section */}
              <div className='bg-cartModalFormBg py-4 px-4 md:px-5  rounded-lg'>
                <span className='flex justify-between gap-x-2 md:gap-x-5 items-center pb-4 md:pb-5'>
                  <div className='flex gap-x-4'>
                    <p className='text-base font-semibold whitespace-nowrap'>
                      Billing Address
                    </p>
                    <ShouldRender check={loading}>
                      <span>
                        <Spin size={screens.md ? 'default' : 'small'} />
                      </span>
                    </ShouldRender>
                  </div>

                  {/* <ShouldRender check={!loading}>
                  <Button
                    size={screens.md ? 'middle' : 'small'}
                    icon={<MapIcon className='h-5 md:w-5 w-4 ' />}
                    loading={currentLocationLoading}
                    onClick={fetchCurrentLocation}
                    className='text-xs md:text-sm'
                  >
                    Locate Me
                  </Button>
                </ShouldRender> */}
                </span>
                <div className=''>
                  <Form.Item
                    name='address'
                    valuePropName='address'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const addressValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (addressValue) {
                            if (addressValue.length <= 190) {
                              return Promise.resolve();
                            } else {
                              return Promise.reject(
                                new Error(
                                  'Address exceeds maximum length of 190 characters!',
                                ),
                              );
                            }
                          } else {
                            return Promise.reject(
                              new Error('Please enter your address!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <Input.TextArea
                      size='large'
                      name='address'
                      value={formData.address}
                      onChange={(e) => handleChange(e)}
                      // onBlur={(e) => handleApiCall('address', e.target.value)}
                      minLength={6}
                      placeholder='Type your complete Address'
                      className='border text-base focus-within:bg-bgColor z-20  placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray    text-white bg-bgColor customInputBlack makeBgBlack'
                    />
                  </Form.Item>
                </div>
                {/* <Form.Item
                  name='phone'
                  valuePropName='phone'
                  rules={[
                    {
                      validator: (_: RuleObject, value: any) => {
                        const phoneNumber =
                          value && typeof value === 'object'
                            ? value.target.value
                            : value;

                        const cleanedPhoneNumber = phoneNumber.replace(
                          /\s/g,
                          '',
                        );

                        const isValidPhoneNumber = /^\d{10}$/.test(
                          cleanedPhoneNumber,
                        );

                        const countryIsIndia =
                          formData.country === 'India' ||
                          Number(formData.country) === 101;

                        if (countryIsIndia && isValidPhoneNumber) {
                          return Promise.resolve();
                        } else if (
                          !countryIsIndia &&
                          cleanedPhoneNumber &&
                          cleanedPhoneNumber.trim() !== ''
                        ) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Please enter a valid phone number!'),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    size='large'
                    name='phone'
                    placeholder='Phone Number'
                    inputMode='numeric'
                    type='tel'
                    autoComplete='tel'
                    value={formData.phone}
                    className='border text-base focus-within:bg-bgColor z-20 placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray   text-white bg-bgColor customInputBlack makeBgBlack'
                    onChange={handleInput('phone', true)}
                    // onBlur={(e) => handleApiCall('phone', e.target.value)}
                  />
                </Form.Item> */}
                <Form.Item
                  name='phone'
                  valuePropName='phone'
                  rules={[
                    {
                      validator: (_) => {
                        if (state.phoneError) {
                          return Promise.reject(new Error(state.phoneError));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <IntlTelInput
                    phone={formData.phone || ''}
                    phoneCountryCode={
                      formData.phoneCountryCode ||
                      globalCountry?.countryCode?.toLowerCase() ||
                      'in'
                    }
                    onChange={({ phone, phoneCountryCode, phonePrefix }) => {
                      const cleanedPhone = phone.replace(/^0+/, '');
                      handlePhoneInput({
                        phone: cleanedPhone,
                        phoneCountryCode,
                        phonePrefix,
                      });
                      form.setFieldsValue({ phone });
                    }}
                    id='phone'
                    // onBlur={() => {
                    //   handleApiCall('phone', formData.phone);
                    //   handleApiCall('iso2', formData.phoneCountryCode || 'in');
                    //   handleApiCall('country_code', formData.phonePrefix || '91');
                    // }}
                  />
                </Form.Item>
                <Form.Item
                  name='country'
                  valuePropName='country'
                  rules={[
                    {
                      validator: (_: RuleObject, value: any) => {
                        const countryValue =
                          value && typeof value === 'object'
                            ? value.target.value
                            : value;
                        if (countryValue) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Please select your country!'),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <CustomSelect
                    value={formData.country}
                    onChange={(value) => {
                      handleSelectChange(value, 'country');
                      // handleApiCall('country_id', value);
                    }}
                    options={countries}
                    placeholder='Select Country'
                    loading={fieldLoading.country}
                    filterOption={filterOption}
                  />
                </Form.Item>
                <Form.Item
                  name='postalcode'
                  valuePropName='postalcode'
                  rules={[
                    {
                      validator: (_, value) => {
                        const enteredPostalCode =
                          value && typeof value === 'object'
                            ? value.target.value
                            : value;

                        const cleanedPostalCode = enteredPostalCode.replace(
                          /\s/g,
                          '',
                        );

                        const countryIsIndia =
                          formData.country === 'India' ||
                          Number(formData.country) === 101;
                        const isValidPostalCodeIndia = /^[0-9]{6}$/.test(
                          cleanedPostalCode,
                        );

                        if (countryIsIndia && isValidPostalCodeIndia) {
                          return Promise.resolve();
                        } else if (
                          !countryIsIndia &&
                          cleanedPostalCode &&
                          cleanedPostalCode.trim() !== ''
                        ) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error('Please enter a valid postal code!'),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    size='large'
                    name='postalcode'
                    inputMode='numeric'
                    placeholder='Postal code'
                    value={formData.postalcode}
                    // onBlur={(e) => handleApiCall('postal_code', e.target.value)}
                    className='border text-base focus-within:bg-bgColor z-20 placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray   text-white bg-bgColor customInputBlack makeBgBlack'
                    onChange={handleInput(
                      'postalcode',
                      formData.country === 'India' ||
                        Number(formData.country) === 101,
                    )}
                  />
                </Form.Item>
                <div className='grid sm:grid-cols-2 sm:gap-4'>
                  <Form.Item
                    name='state'
                    valuePropName='state'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const stateValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (stateValue) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error('Please select your state!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <CustomSelect
                      value={formData.state === '' ? undefined : formData.state}
                      onChange={(value) => {
                        handleSelectChange(value, 'state');
                        // handleApiCall('state_id', value);
                      }}
                      options={states}
                      placeholder='Select State'
                      disabled={!formData?.country}
                      loading={fieldLoading.state}
                      name='state'
                      filterOption={filterOption}
                    />
                  </Form.Item>

                  <Form.Item
                    name='city'
                    valuePropName='city'
                    rules={[
                      {
                        validator: (_: RuleObject, value: any) => {
                          const cityValue =
                            value && typeof value === 'object'
                              ? value.target.value
                              : value;

                          if (cityValue) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(
                              new Error('Please select your city!'),
                            );
                          }
                        },
                      },
                    ]}
                  >
                    <CustomSelect
                      value={formData.city === '' ? undefined : formData.city}
                      onChange={(value) => {
                        handleSelectChange(value, 'city');
                        // handleApiCall('city_id', value);
                      }}
                      options={cities}
                      placeholder='Select City'
                      loading={fieldLoading.city}
                      disabled={!formData?.state}
                      filterOption={filterOption}
                      name='city'
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
      <div className='  lg:min-h-[93vh]   pt-5 lg:pt-0 lg:col-span-1 col-span-2 border-borderDarkGray '>
        <div
          className={clsx(
            // isCheckoutPage ? 'pb-4' : 'md:py-4',
            ' text-fadedWhite px-4 lg:px-0  w-full  flex flex-col gap-y-2  rounded-md cardShadow ',
          )}
        >
          <div
            className={clsx(
              // isCheckoutPage ? 'gap-y-2 xl:gap-y-5' : 'gap-y-5',
              'flex flex-col mb-0 md:px-8 lg:px-4 w-full',
            )}
          >
            {affixContent}
          </div>
          <div
            className={clsx(
              // isCheckoutPage ? 'gap-y-2 xl:gap-y-5' : 'gap-y-5',
              'flex flex-col mb-8 md:px-8 lg:px-4 w-full',
            )}
          >
            <ButtonDefault
              size={0}
              rounded={false}
              roundedSm={true}
              enableScaling={false}
              disabled={submitLoading}
              loading={submitLoading}
              shimmerButton={true}
              onClick={submitHandle}
              variant={ButtonVariants.PRIMARY}
              className='py-3'
            >
              Proceed to Pay
            </ButtonDefault>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubsctiptionCheckout;
